import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { X, Save, Users, FileText, Tag } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'


import type { Ministerio } from '@/types'

interface MinisterioFormCardProps {
  onSave: (ministerio: Omit<Ministerio, 'id' | 'created_at' | 'updated_at'>) => void
  onCancel: () => void
}



export function MinisterioFormCard({ onSave, onCancel }: MinisterioFormCardProps) {
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!nome.trim() || !descricao.trim()) {
      return
    }

    setIsLoading(true)
    
    try {
      const novoMinisterio: Omit<Ministerio, 'id' | 'created_at' | 'updated_at'> = {
        nome: nome.trim(),
        descricao: descricao.trim(),
        grupoFuncoes: 'outros', // Valor padrão
        lider_perfil_id: null,
        igreja_id: '123e4567-e89b-12d3-a456-426614174002', // ID padrão da igreja
        ativo: true
      }

      await onSave(novoMinisterio)
      
      // Limpar formulário
      setNome('')
      setDescricao('')
    } catch (error) {
      console.error('Erro ao salvar ministério:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <Card className="border-2 border-primary/20 shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Novo Ministério</CardTitle>
                <CardDescription>
                  Preencha as informações para criar um novo ministério
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nome do Ministério */}
            <div className="space-y-2">
              <Label htmlFor="nome" className="flex items-center space-x-2">
                <Tag className="h-4 w-4" />
                <span>Nome do Ministério</span>
              </Label>
              <Input
                id="nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Ministério de Louvor"
                required
                className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="descricao" className="flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Descrição</span>
              </Label>
              <Textarea
                id="descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descreva o propósito e atividades do ministério..."
                rows={4}
                required
                className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 resize-none"
              />
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!nome.trim() || !descricao.trim() || isLoading}
                className="min-w-[120px]"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Salvando...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Save className="h-4 w-4" />
                    <span>Salvar Ministério</span>
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  )
}